import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { OtpStep } from "@/components/OtpStep";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [otp, setOtp] = useState(null);
  const nav = useNavigate();
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("idle") === "1") toast.info("You were signed out after a period of inactivity.");
  }, [params]);

  const [locked, setLocked] = useState(false);
  useEffect(() => {
    if (!loading && user) {
      if (locked) nav("/admin/billing?locked=1");
      else if (user.role === "platform_owner") nav("/platform");
      else if (user.role === "admin" || user.role === "corporate_admin") nav("/admin");
      else if (user.role === "teacher") nav("/teacher");
      else if (user.role === "driver") nav("/driver");
      else nav("/portal");
    }
  }, [user, loading, nav, locked]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok && res.otpRequired) { setOtp(res); return; }
    afterLogin(res);
  };

  const afterLogin = (res) => {
    if (res.ok) {
      if (res.subscriptionLocked) { setLocked(true); toast.error("Your plan has expired — payment is required to continue."); }
      else toast.success("Welcome back");
      // user state will be set; useEffect handles the redirect by role
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-navy-hero flex flex-col" data-testid="login-page">
      <div className="px-8 py-6 flex items-center justify-between gap-4">
        <div className="inline-flex items-center gap-2 text-slate-700" data-testid="login-secured-pill">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span className="text-xs uppercase tracking-widest">Secured access · sign-in required</span>
        </div>
        <Link to="/parent/login" data-testid="login-parent-portal-btn" className="inline-flex items-center gap-2 rounded-full border border-sky-300/60 bg-white/80 backdrop-blur px-4 py-2 text-sm font-semibold text-primary shadow-sm hover:bg-primary hover:text-white transition">
          <Users className="h-4 w-4" /> Parent login
        </Link>
      </div>
      <div className="flex flex-1 items-center justify-center px-6">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-10 shadow-xl shadow-sky-900/15">
          <div className="flex items-center gap-3 mb-8">
            <img src="/logerd-logo.png" alt="Logerd" className="h-14 w-14 object-contain flex-shrink-0" />
            <div className="font-display text-2xl font-bold tracking-tight text-slate-900">Logerd</div>
          </div>

          {otp ? <OtpStep challenge={otp} onBack={() => setOtp(null)} onDone={afterLogin} /> : (
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <label className="block">
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full h-12 rounded-lg border border-slate-200 bg-white px-4 text-base font-body focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </label>
            <label className="block">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-widest text-slate-500">Password</span>
                <a href="/forgot-password" className="text-xs font-medium text-primary hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </a>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full h-12 rounded-lg border border-slate-200 bg-white px-4 text-base font-body focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full h-12 rounded-lg bg-primary text-white font-display text-base font-semibold tracking-tight transition active:scale-[0.98] hover:shadow disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign in
            </button>
          </form>
          )}

          <div className="mt-6 text-xs text-slate-500 border-t border-slate-100 pt-4 flex items-center justify-between">
            <span>New here?</span>
            <a href="/register" className="text-primary font-medium hover:underline" data-testid="register-link">Register →</a>
          </div>
        </div>
      </div>
    </div>
  );
}
